"use client";

import React, { useCallback, useMemo, useState } from "react";
import {
  renderBooking,
  interceptPreviewClicks,
  AppointmentReviewPage,
} from "@openings/template-components";
import type {
  ScheduleBookingConfig,
  MemberBookingConfig,
} from "@openings/template-components";
import type { TemplateData, SalonInfo } from "@openings/template-components";

import { ServicesBookingSection } from "./ServicesBookingSection";
import type {
  BookingInfoBlog,
  BookingInfoSchedule,
  BookingInfoMember,
} from "./BookingInfoPanel";
import { BookingPageHeaderWithDrawer } from "./BookingPageHeaderWithDrawer";
import { BookingPageWrapper } from "./BookingPageWrapper";
import { EditorialServiceLedger } from "./EditorialServiceLedger";
import { standaloneCardTitleStyle } from "./editorialBookingStyles";
import { servicesOfferedAtSchedule } from "./scheduleServices";

type ServiceGroup = {
  key: string;
  name: string | null;
  services: Array<{
    id: string;
    title: string;
    description?: string;
    price?: number;
    up?: boolean;
    duration?: number;
    categoryId?: string;
  }>;
};

function groupServicesByCategory(
  services: ServiceGroup["services"],
  categories?: { id: string; name: string; sortOrder: number }[],
): ServiceGroup[] {
  if (!categories?.length) {
    return [{ key: "all", name: null, services }];
  }

  const sorted = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);
  const grouped = new Map<string, ServiceGroup["services"]>();
  const uncategorized: ServiceGroup["services"] = [];

  for (const service of services) {
    if (service.categoryId) {
      const existing = grouped.get(service.categoryId) || [];
      existing.push(service);
      grouped.set(service.categoryId, existing);
    } else {
      uncategorized.push(service);
    }
  }

  const groups: ServiceGroup[] = [];
  for (const cat of sorted) {
    const catServices = grouped.get(cat.id);
    if (catServices?.length) {
      groups.push({ key: cat.id, name: cat.name, services: catServices });
    }
  }
  if (uncategorized.length > 0) {
    groups.push({ key: "uncategorized", name: null, services: uncategorized });
  }

  return groups;
}

// ---------------------------------------------------------------------------
// BookingPage — single-page centered layout (subdomain booking)
// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type BookingPageProps = {
  route: string;
  salon: SalonInfo;
  data?: TemplateData;
  booking?: ScheduleBookingConfig | MemberBookingConfig;
  mode?: "live" | "preview";
  homeHref?: string;
  blog?: BookingInfoBlog | null;
};

// Standalone booking-page shell: header/body padding and the bordered card.
// Shared by the booking branch and the appointment review branch so both
// sit in the same frame.
const BOOKING_PAGE_SHELL_CSS = `
        .openings-booking-wrapper.openings-booking-header-wrapper {
          padding-top:
            var(--openings-booking-editorial-outer-padding, 32px);
        }
        .openings-booking-wrapper.openings-booking-body-wrapper {
          padding-bottom:
            var(--openings-booking-editorial-outer-padding, 32px);
        }
        .openings-booking-body {
          display: grid;
          grid-template-columns: minmax(0, 1fr);
          grid-template-areas:
            "booking"
            "ledger";
          gap: var(--openings-booking-spacing-lg, 24px);
          align-items: start;
        }
        .openings-booking-body--booking-only {
          grid-template-areas: "booking";
        }
        .openings-booking-rail {
          grid-area: booking;
          position: static;
          min-width: 0;
          overflow: visible;
        }
        .openings-booking-card {
          box-sizing: border-box;
          width: 100%;
          border: 1px solid var(--openings-color-border, #e5e5e5);
          box-shadow: var(--openings-booking-surface-shadow, none);
          border-radius: var(--openings-booking-radius, 10px);
          background: var(--openings-color-surface, #fff);
        }
        .openings-booking-card {
          padding: var(--openings-booking-editorial-card-padding, 16px);
          --openings-booking-unavailable-member-padding-y:
            var(--openings-booking-spacing-sm, 8px);
        }
        .openings-booking-card-intro {
          display: flex;
          flex-direction: column;
          margin-bottom: var(--openings-booking-spacing-lg, 24px);
        }
        .openings-booking-card-intro h1 {
          color: var(--openings-color-text, #111);
        }
        .openings-booking-card .openings-services-booking-section {
          padding: 0;
        }

        @media (min-width: 768px) {
          .openings-booking-body {
            gap: var(--openings-booking-spacing-lg, 24px);
          }
          .openings-booking-card {
            padding: var(--openings-booking-editorial-card-padding, 32px);
          }
        }

        @media (min-width: 992px) {
          .openings-booking-body {
            grid-template-columns:
              minmax(0, 1fr)
              clamp(320px, 38vw, 420px);
            grid-template-areas: "ledger booking";
          }
          .openings-booking-body--booking-only {
            grid-template-columns: minmax(0, 420px);
            grid-template-areas: "booking";
            justify-content: end;
          }
          .openings-booking-rail {
            position: sticky;
            top: var(--openings-booking-spacing-lg, 24px);
            align-self: start;
          }
        }

        @media (max-width: 991px) {
          .openings-booking-wrapper.openings-booking-header-wrapper {
            padding-top:
              var(--openings-booking-editorial-outer-padding, 16px);
            padding-right:
              var(--openings-booking-editorial-outer-padding, 16px);
            padding-bottom: var(--openings-booking-spacing-sm, 8px);
            padding-left:
              var(--openings-booking-editorial-outer-padding, 16px);
          }
          .openings-booking-wrapper.openings-booking-body-wrapper {
            padding-top: var(--openings-booking-spacing-sm, 8px);
            padding-right:
              var(--openings-booking-editorial-outer-padding, 16px);
            padding-bottom:
              var(--openings-booking-editorial-outer-padding, 16px);
            padding-left:
              var(--openings-booking-editorial-outer-padding, 16px);
          }
          .openings-booking-card:has(.openings-appointment-header)
            .openings-booking-card-intro {
            margin-bottom: 0;
          }
          .openings-booking-card:has(.openings-appointment-header)
            .openings-booking-card-intro h1 {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px !important;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
          }
        }
`;

// ---------------------------------------------------------------------------

export default function BookingPage({
  route,
  salon,
  data,
  booking,
  mode = "live",
  homeHref,
  blog,
}: BookingPageProps) {
  const isPreview = mode === "preview";
  const rootClass = "openings-booking-page";

  const bookingConfig = booking;

  // Track the actively selected schedule to sync the info drawer
  // (hooks must be before any early return)
  const initialScheduleId =
    bookingConfig?.type === "schedule"
      ? (bookingConfig.initialScheduleId ?? null)
      : null;
  const [activeScheduleId, setActiveScheduleId] = useState<string | null>(
    initialScheduleId,
  );
  const handleScheduleSelect = useCallback((id: string | null) => {
    setActiveScheduleId(id);
  }, []);

  // Inject onScheduleSelect into the booking config so ScheduleBooking reports back
  const augmentedBookingConfig = useMemo(() => {
    if (!bookingConfig) return undefined;
    if (bookingConfig.type === "schedule") {
      return { ...bookingConfig, onScheduleSelect: handleScheduleSelect };
    }
    return bookingConfig;
  }, [bookingConfig, handleScheduleSelect]);

  // Build services list for info panel
  const allServices = (data?.bookingPreview?.services ?? []).map((s) => ({
    id: s.id,
    title: s.title,
    description: s.description ?? undefined,
    price: s.price,
    up: s.up,
    duration: s.duration,
    categoryId: s.categoryId ?? undefined,
  }));

  const scheduleAssignedServiceIds =
    data?.bookingPreview?.scheduleAssignedServiceIds;

  // Filter services: when a schedule is selected, show only the services it
  // offers (all of them when it has none assigned, as the picker does).
  const services = useMemo(() => {
    if (!activeScheduleId) return allServices;
    return servicesOfferedAtSchedule(
      allServices,
      scheduleAssignedServiceIds?.[activeScheduleId],
    );
  }, [allServices, activeScheduleId, scheduleAssignedServiceIds]);

  // Info drawer content, shared by the review and booking branches.
  const schedules: BookingInfoSchedule[] = (
    data?.bookingPreview?.locations ?? []
  ).map((s) => ({
    id: s.id,
    title: s.title,
    address: s.address ?? undefined,
    parking: s.parking ?? undefined,
    phoneNumber: s.phone ?? undefined,
    instagramHandle: s.instagramHandle ?? undefined,
  }));

  const members: BookingInfoMember[] = (
    data?.bookingPreview?.members ?? []
  ).reduce<BookingInfoMember[]>((acc, m) => {
    if (!acc.some((x) => x.id === m.id)) {
      acc.push({ id: m.id, name: m.name, photo: m.photo ?? null });
    }
    return acc;
  }, []);

  // --- Review page: render review data in a centered layout ---
  if (route === "/appointment-review" && data?.appointmentReview) {
    return (
      <div
        className={rootClass}
        {...(isPreview ? { onClickCapture: interceptPreviewClicks } : {})}
      >
        <style>{BOOKING_PAGE_SHELL_CSS}</style>
        <BookingPageWrapper className="openings-booking-header-wrapper">
          <BookingPageHeaderWithDrawer
            salonName={salon.name}
            salonLogoUrl={salon.logoUrl}
            homeHref={homeHref}
            infoPanelProps={{
              schedules,
              services: allServices,
              members,
              phoneNumber: data?.bookingPreview?.phoneNumber,
              supportEmail: data?.bookingPreview?.supportEmail,
              instagramHandle: data?.bookingPreview?.instagramHandle,
            }}
          />
        </BookingPageWrapper>
        <BookingPageWrapper className="openings-booking-body-wrapper">
          <div className="openings-booking-card" data-review-card>
            <AppointmentReviewPage data={data.appointmentReview} />
          </div>
        </BookingPageWrapper>
      </div>
    );
  }

  const serviceCategories = (data?.bookingPreview?.serviceCategories ?? []) as {
    id: string;
    name: string;
    sortOrder: number;
  }[];

  // Group services by category for the left column
  const serviceGroups = groupServicesByCategory(services, serviceCategories);

  // Filter info panel to show only the active schedule when one is selected
  const activeSchedule = activeScheduleId
    ? (schedules.find((s) => s.id === activeScheduleId) ?? null)
    : null;
  const infoPanelSchedules = activeSchedule ? [activeSchedule] : schedules;

  const body = (
    <ServicesBookingSection
      title={undefined}
      subtitle={undefined}
      note={undefined}
      services={services}
    >
      {augmentedBookingConfig && renderBooking(augmentedBookingConfig)}
    </ServicesBookingSection>
  );

  return (
    <div
      className={rootClass}
      {...(isPreview ? { onClickCapture: interceptPreviewClicks } : {})}
    >
      <style>{BOOKING_PAGE_SHELL_CSS}</style>

      {/* ── Header ── */}
      <BookingPageWrapper className="openings-booking-header-wrapper">
        <BookingPageHeaderWithDrawer
          salonName={salon.name}
          salonLogoUrl={salon.logoUrl}
          homeHref={homeHref}
          infoPanelProps={{
            schedules: infoPanelSchedules,
            services,
            members,
            phoneNumber: data?.bookingPreview?.phoneNumber,
            supportEmail: data?.bookingPreview?.supportEmail,
            instagramHandle: data?.bookingPreview?.instagramHandle,
            blog,
          }}
        />
      </BookingPageWrapper>

      {/* ── Body grid: info left, booking right ── */}
      <BookingPageWrapper className="openings-booking-body-wrapper">
        <div
          className={`openings-booking-body${
            services.length === 0
              ? " openings-booking-body--booking-only"
              : ""
          }`}
          data-booking-layout
        >
          <section
            className="openings-booking-rail"
            data-booking-rail
            aria-labelledby="openings-booking-card-title"
          >
            <div className="openings-booking-card" data-booking-card>
              <div className="openings-booking-card-intro">
                <h1
                  id="openings-booking-card-title"
                  style={standaloneCardTitleStyle}
                >
                  Book an appointment
                </h1>
              </div>
              {body}
            </div>
          </section>

          {services.length > 0 ? (
            <EditorialServiceLedger groups={serviceGroups} />
          ) : null}
        </div>
      </BookingPageWrapper>
    </div>
  );
}
